import Dexie, { Table } from 'dexie'
import type {
  UserProfile,
  JobApplication,
  FormSchema,
  AppSettings,
  Notification,
  SyncStatus,
  CoverLetter,
  ContactRecord,
  OnboardingState
} from '../types'

// ============================================================================
// IndexedDB Schema (Local-First Storage)
// ============================================================================

export class JobFlowDatabase extends Dexie {
  // Core tables
  profiles!: Table<UserProfile>
  applications!: Table<JobApplication>
  formSchemas!: Table<FormSchema>
  settings!: Table<AppSettings & { id: string }>
  
  // New tables for auth & notifications
  notifications!: Table<Notification>
  syncQueue!: Table<SyncQueueItem>
  syncStatus!: Table<SyncStatus & { id: string }>

  // Usage tracking for payments
  usage!: Table<UsageRecord>

  // Cover letters
  coverLetters!: Table<CoverLetter>

  // Contacts CRM
  contacts!: Table<ContactRecord>

  // Onboarding state
  onboarding!: Table<OnboardingState>

  constructor() {
    super('JobFlowDB')
    
    // Version 2 adds notifications and sync tables
    this.version(2).stores({
      profiles: 'id, createdAt, updatedAt',
      applications: 'id, company, status, stage, appliedDate, createdAt, followUpDate',
      formSchemas: 'id, domain, urlPattern',
      settings: 'id',
      // New tables
      notifications: 'id, userId, type, priority, scheduledFor, readAt, createdAt, [userId+readAt]',
      syncQueue: '++id, entityType, entityId, action, createdAt',
      syncStatus: 'id'
    })

    // Version 3 adds usage tracking for payments
    this.version(3).stores({
      profiles: 'id, createdAt, updatedAt',
      applications: 'id, company, status, stage, appliedDate, createdAt, followUpDate',
      formSchemas: 'id, domain, urlPattern',
      settings: 'id',
      notifications: 'id, userId, type, priority, scheduledFor, readAt, createdAt, [userId+readAt]',
      syncQueue: '++id, entityType, entityId, action, createdAt',
      syncStatus: 'id',
      usage: 'id, month'
    })

    // Version 4 adds cover letters
    this.version(4).stores({
      profiles: 'id, createdAt, updatedAt',
      applications: 'id, company, status, stage, appliedDate, createdAt, followUpDate',
      formSchemas: 'id, domain, urlPattern',
      settings: 'id',
      notifications: 'id, userId, type, priority, scheduledFor, readAt, createdAt, [userId+readAt]',
      syncQueue: '++id, entityType, entityId, action, createdAt',
      syncStatus: 'id',
      usage: 'id, month',
      coverLetters: 'id, applicationId, createdAt'
    })

    // Version 5 adds contacts CRM
    this.version(5).stores({
      profiles: 'id, createdAt, updatedAt',
      applications: 'id, company, status, stage, appliedDate, createdAt, followUpDate',
      formSchemas: 'id, domain, urlPattern',
      settings: 'id',
      notifications: 'id, userId, type, priority, scheduledFor, readAt, createdAt, [userId+readAt]',
      syncQueue: '++id, entityType, entityId, action, createdAt',
      syncStatus: 'id',
      usage: 'id, month',
      coverLetters: 'id, applicationId, createdAt',
      contacts: 'id, name, company, contactType, createdAt'
    })

    // Version 6 adds onboarding state
    this.version(6).stores({
      profiles: 'id, createdAt, updatedAt',
      applications: 'id, company, status, stage, appliedDate, createdAt, followUpDate',
      formSchemas: 'id, domain, urlPattern',
      settings: 'id',
      notifications: 'id, userId, type, priority, scheduledFor, readAt, createdAt, [userId+readAt]',
      syncQueue: '++id, entityType, entityId, action, createdAt',
      syncStatus: 'id',
      usage: 'id, month',
      coverLetters: 'id, applicationId, createdAt',
      contacts: 'id, name, company, contactType, createdAt',
      onboarding: 'id'
    })
  }
}

// Usage record for payment/plan tracking
export interface UsageRecord {
  id: string
  month: string // YYYY-MM
  fillCount: number
  plan: 'free' | 'pro'
  stripeCustomerId: string | null
}

// Sync queue for offline-first sync
export interface SyncQueueItem {
  id?: number
  entityType: 'application' | 'profile' | 'settings' | 'notification' | 'contact'
  entityId: string
  action: 'create' | 'update' | 'delete'
  payload: Record<string, unknown>
  createdAt: Date
  retryCount: number
  lastError?: string
}

export const db = new JobFlowDatabase()

// ============================================================================
// Database Initialization
// ============================================================================

export function getDefaultNotificationPreferences(): import('../types').NotificationPreferences {
  return {
    enabled: true,
    followUpReminderDays: 7,
    quietHours: {
      enabled: false,
      startHour: 22,
      startMinute: 0,
      endHour: 8,
      endMinute: 0
    },
    perType: {
      follow_up_due: { enabled: true, channels: ['browser', 'in_app'], snoozeDurationMinutes: 60 },
      application_status_change: { enabled: true, channels: ['browser', 'in_app'], snoozeDurationMinutes: 0 },
      weekly_digest: { enabled: true, channels: ['in_app'], snoozeDurationMinutes: 0 }
    }
  }
}

export function getDefaultSettings(): import('../types').AppSettings {
  return {
    notifications: getDefaultNotificationPreferences(),
    autofill: { autoSubmit: false, confirmBeforeFill: true },
    theme: 'system'
  }
}

export async function initializeDatabase() {
  const settingsCount = await db.settings.count()
  if (settingsCount === 0) {
    await db.settings.add({ ...getDefaultSettings(), id: 'default' })
  }
  
  const syncStatusCount = await db.syncStatus.count()
  if (syncStatusCount === 0) {
    await db.syncStatus.add({
      id: 'default',
      lastSyncAt: null,
      syncInProgress: false,
      syncEnabled: false,
      pendingChanges: 0,
      lastError: null
    })
  }
}

// ============================================================================
// Profile Repository
// ============================================================================

export const profileRepository = {
  async get(): Promise<UserProfile | undefined> {
    return db.profiles.toCollection().first()
  },

  async save(profile: UserProfile): Promise<string> {
    profile.updatedAt = new Date()
    await db.profiles.put(profile)
    return profile.id
  },

  async create(profile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const newProfile: UserProfile = {
      ...profile,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    }
    await db.profiles.add(newProfile)
    return newProfile.id
  },

  async delete(): Promise<void> {
    await db.profiles.clear()
  }
}

// ============================================================================
// Application Repository
// ============================================================================

export const applicationRepository = {
  async getAll(): Promise<JobApplication[]> {
    return db.applications.orderBy('appliedDate').reverse().toArray()
  },

  async getById(id: string): Promise<JobApplication | undefined> {
    return db.applications.get(id)
  },

  async getByStage(stage: JobApplication['stage']): Promise<JobApplication[]> {
    return db.applications.where('stage').equals(stage).toArray()
  },

  async getActive(): Promise<JobApplication[]> {
    return db.applications.where('status').equals('active').toArray()
  },

  async getFollowUpsDue(date: Date): Promise<JobApplication[]> {
    const dateStr = date.toISOString().split('T')[0]
    return db.applications
      .where('followUpDate')
      .belowOrEqual(dateStr)
      .filter(app => app.status === 'active')
      .toArray()
  },

  async save(application: JobApplication): Promise<string> {
    application.updatedAt = new Date()
    await db.applications.put(application)
    return application.id
  },

  async create(application: Omit<JobApplication, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const newApp: JobApplication = {
      ...application,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    }
    await db.applications.add(newApp)
    return newApp.id
  },

  async delete(id: string): Promise<void> {
    await db.applications.delete(id)
  },

  async updateStage(id: string, stage: JobApplication['stage']): Promise<void> {
    await db.applications.update(id, { stage, updatedAt: new Date() })
  },

  async updateFollowUpDate(id: string, followUpDate: string | undefined): Promise<void> {
    await db.applications.update(id, { followUpDate, updatedAt: new Date() })
  }
}

// ============================================================================
// Notification Repository
// ============================================================================

export const notificationRepository = {
  async getAll(): Promise<Notification[]> {
    return db.notifications.orderBy('createdAt').reverse().toArray()
  },

  async getUnread(): Promise<Notification[]> {
    return db.notifications
      .where('[userId+readAt]')
      .between(['', ''], ['', '\uffff'])  // readAt is null (empty string in indexeddb)
      .toArray()
      .then(items => items.filter(n => n.readAt === null))
  },

  async getUnreadCount(): Promise<number> {
    const all = await db.notifications.toArray()
    return all.filter(n => n.readAt === null).length
  },

  async getScheduledDue(date: Date): Promise<Notification[]> {
    return db.notifications
      .where('scheduledFor')
      .belowOrEqual(date)
      .filter(n => n.readAt === null && n.scheduledFor !== null)
      .toArray()
  },

  async getById(id: string): Promise<Notification | undefined> {
    return db.notifications.get(id)
  },

  async create(notification: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    }
    await db.notifications.add(newNotification)
    return newNotification.id
  },

  async markRead(id: string): Promise<void> {
    await db.notifications.update(id, { readAt: new Date(), updatedAt: new Date() })
  },

  async markAllRead(): Promise<void> {
    const unread = await this.getUnread()
    const now = new Date()
    await Promise.all(
      unread.map(n => db.notifications.update(n.id, { readAt: now, updatedAt: now }))
    )
  },

  async dismiss(id: string): Promise<void> {
    await db.notifications.update(id, { dismissedAt: new Date(), updatedAt: new Date() })
  },

  async delete(id: string): Promise<void> {
    await db.notifications.delete(id)
  },

  async clearAll(): Promise<void> {
    await db.notifications.clear()
  }
}

// ============================================================================
// Form Schema Repository
// ============================================================================

export const formSchemaRepository = {
  async getAll(): Promise<FormSchema[]> {
    return db.formSchemas.toArray()
  },

  async getById(id: string): Promise<FormSchema | undefined> {
    return db.formSchemas.get(id)
  },

  async getByDomain(domain: string): Promise<FormSchema[]> {
    return db.formSchemas.where('domain').equals(domain).toArray()
  },

  async findByUrl(url: string): Promise<FormSchema | undefined> {
    const schemas = await db.formSchemas.toArray()
    return schemas.find(schema => {
      try {
        return new RegExp(schema.urlPattern).test(url)
      } catch {
        return url.includes(schema.urlPattern)
      }
    })
  },

  async create(schema: Omit<FormSchema, 'id' | 'createdAt'>): Promise<string> {
    const newSchema: FormSchema = {
      ...schema,
      id: crypto.randomUUID(),
      createdAt: new Date()
    }
    await db.formSchemas.add(newSchema)
    return newSchema.id
  },

  async save(schema: FormSchema): Promise<string> {
    await db.formSchemas.put(schema)
    return schema.id
  },

  async delete(id: string): Promise<void> {
    await db.formSchemas.delete(id)
  },

  async deleteByDomain(domain: string): Promise<void> {
    await db.formSchemas.where('domain').equals(domain).delete()
  }
}

// ============================================================================
// Settings Repository
// ============================================================================

export const settingsRepository = {
  async get(): Promise<AppSettings> {
    const settings = await db.settings.get('default')
    if (!settings) return getDefaultSettings()
    // Migrate legacy settings that lack the new notification shape
    const defaults = getDefaultSettings()
    if (!settings.notifications.perType) {
      settings.notifications = { ...defaults.notifications, ...settings.notifications }
    }
    if (!settings.notifications.quietHours) {
      settings.notifications.quietHours = defaults.notifications.quietHours
    }
    return settings
  },

  async save(settings: AppSettings): Promise<void> {
    await db.settings.put({ ...settings, id: 'default' })
  }
}

// ============================================================================
// Sync Queue Repository
// ============================================================================

export const syncQueueRepository = {
  async getAll(): Promise<SyncQueueItem[]> {
    return db.syncQueue.orderBy('createdAt').toArray()
  },

  async getCount(): Promise<number> {
    return db.syncQueue.count()
  },

  async add(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'retryCount'>): Promise<number> {
    return db.syncQueue.add({
      ...item,
      createdAt: new Date(),
      retryCount: 0
    }) as Promise<number>
  },

  async incrementRetry(id: number, error: string): Promise<void> {
    const item = await db.syncQueue.get(id)
    if (item) {
      await db.syncQueue.update(id, {
        retryCount: item.retryCount + 1,
        lastError: error
      })
    }
  },

  async remove(id: number): Promise<void> {
    await db.syncQueue.delete(id)
  },

  async clear(): Promise<void> {
    await db.syncQueue.clear()
  }
}

// ============================================================================
// Sync Status Repository
// ============================================================================

export const syncStatusRepository = {
  async get(): Promise<SyncStatus> {
    const status = await db.syncStatus.get('default')
    return status || {
      lastSyncAt: null,
      syncInProgress: false,
      syncEnabled: false,
      pendingChanges: 0,
      lastError: null
    }
  },

  async update(updates: Partial<SyncStatus>): Promise<void> {
    const current = await this.get()
    await db.syncStatus.put({ ...current, ...updates, id: 'default' })
  },

  async setSyncEnabled(enabled: boolean): Promise<void> {
    await this.update({ syncEnabled: enabled })
  },

  async setSyncInProgress(inProgress: boolean): Promise<void> {
    await this.update({ syncInProgress: inProgress })
  },

  async recordSync(success: boolean, error?: string): Promise<void> {
    await this.update({
      lastSyncAt: success ? new Date() : undefined,
      syncInProgress: false,
      lastError: error || null
    })
  }
}

// ============================================================================
// Cover Letter Repository
// ============================================================================

export const coverLetterRepository = {
  async get(id: string): Promise<CoverLetter | undefined> {
    return db.coverLetters.get(id)
  },

  async getByApplicationId(applicationId: string): Promise<CoverLetter | undefined> {
    return db.coverLetters.where('applicationId').equals(applicationId).first()
  },

  async save(coverLetter: CoverLetter): Promise<string> {
    coverLetter.updatedAt = new Date()
    await db.coverLetters.put(coverLetter)
    return coverLetter.id
  },

  async create(coverLetter: Omit<CoverLetter, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const newLetter: CoverLetter = {
      ...coverLetter,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    }
    await db.coverLetters.add(newLetter)
    return newLetter.id
  },

  async delete(id: string): Promise<void> {
    await db.coverLetters.delete(id)
  }
}

// ============================================================================
// Contact Repository
// ============================================================================

export const contactRepository = {
  async getAll(): Promise<ContactRecord[]> {
    return db.contacts.orderBy('createdAt').reverse().toArray()
  },

  async getById(id: string): Promise<ContactRecord | undefined> {
    return db.contacts.get(id)
  },

  async search(query: string): Promise<ContactRecord[]> {
    const all = await db.contacts.toArray()
    const q = query.toLowerCase()
    return all.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.role.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.notes.toLowerCase().includes(q)
    )
  },

  async getByApplicationId(applicationId: string): Promise<ContactRecord[]> {
    const all = await db.contacts.toArray()
    return all.filter(c => c.linkedApplicationIds.includes(applicationId))
  },

  async create(contact: Omit<ContactRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const newContact: ContactRecord = {
      ...contact,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    }
    await db.contacts.add(newContact)
    return newContact.id
  },

  async save(contact: ContactRecord): Promise<string> {
    contact.updatedAt = new Date()
    await db.contacts.put(contact)
    return contact.id
  },

  async update(id: string, updates: Partial<Omit<ContactRecord, 'id' | 'createdAt'>>): Promise<void> {
    await db.contacts.update(id, { ...updates, updatedAt: new Date() })
  },

  async addInteraction(contactId: string, interaction: Omit<import('../types').InteractionRecord, 'id'>): Promise<void> {
    const contact = await db.contacts.get(contactId)
    if (!contact) throw new Error(`Contact ${contactId} not found`)
    const newInteraction: import('../types').InteractionRecord = {
      ...interaction,
      id: crypto.randomUUID()
    }
    await db.contacts.update(contactId, {
      interactions: [...contact.interactions, newInteraction],
      updatedAt: new Date()
    })
  },

  async removeInteraction(contactId: string, interactionId: string): Promise<void> {
    const contact = await db.contacts.get(contactId)
    if (!contact) throw new Error(`Contact ${contactId} not found`)
    await db.contacts.update(contactId, {
      interactions: contact.interactions.filter(i => i.id !== interactionId),
      updatedAt: new Date()
    })
  },

  async linkApplication(contactId: string, applicationId: string): Promise<void> {
    const contact = await db.contacts.get(contactId)
    if (!contact) throw new Error(`Contact ${contactId} not found`)
    if (!contact.linkedApplicationIds.includes(applicationId)) {
      await db.contacts.update(contactId, {
        linkedApplicationIds: [...contact.linkedApplicationIds, applicationId],
        updatedAt: new Date()
      })
    }
  },

  async unlinkApplication(contactId: string, applicationId: string): Promise<void> {
    const contact = await db.contacts.get(contactId)
    if (!contact) throw new Error(`Contact ${contactId} not found`)
    await db.contacts.update(contactId, {
      linkedApplicationIds: contact.linkedApplicationIds.filter(id => id !== applicationId),
      updatedAt: new Date()
    })
  },

  async delete(id: string): Promise<void> {
    await db.contacts.delete(id)
  }
}

// ============================================================================
// Onboarding Repository
// ============================================================================

export const onboardingRepository = {
  async get(): Promise<OnboardingState | undefined> {
    return db.onboarding.toCollection().first()
  },

  async isComplete(): Promise<boolean> {
    const state = await db.onboarding.toCollection().first()
    return state?.skipped === true || state?.currentStep === 'completed'
  },

  async initialize(): Promise<string> {
    const existing = await this.get()
    if (existing) return existing.id

    const now = new Date()
    const state: OnboardingState = {
      id: 'default',
      currentStep: 'import',
      completedSteps: [],
      importMethod: null,
      skipped: false,
      createdAt: now,
      updatedAt: now
    }
    await db.onboarding.add(state)
    return state.id
  },

  async updateStep(step: OnboardingState['currentStep'], completedStep?: import('../types').OnboardingStep): Promise<void> {
    const state = await this.get()
    if (!state) return
    const completedSteps = completedStep && !state.completedSteps.includes(completedStep)
      ? [...state.completedSteps, completedStep]
      : state.completedSteps
    await db.onboarding.update('default', {
      currentStep: step,
      completedSteps,
      updatedAt: new Date()
    })
  },

  async setImportMethod(method: OnboardingState['importMethod']): Promise<void> {
    await db.onboarding.update('default', {
      importMethod: method,
      updatedAt: new Date()
    })
  },

  async complete(): Promise<void> {
    await db.onboarding.update('default', {
      currentStep: 'completed',
      completedSteps: ['import', 'profile', 'extension', 'test-autofill'],
      updatedAt: new Date()
    })
  },

  async skip(): Promise<void> {
    await db.onboarding.update('default', {
      skipped: true,
      updatedAt: new Date()
    })
  }
}
